import { Injectable } from '@angular/core';
import { timer, BehaviorSubject } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { startWith, switchMap, map, share, distinctUntilChanged } from 'rxjs/operators';

export enum TimeDisplayType {
    LOCAL = 'LOCAL_TIME',
    UTC = 'UTC_TIME',
}

export const TimeDisplayData = {
    [TimeDisplayType.LOCAL]: {
        label: 'My local time',
        icon: 's-clock',
        type: TimeDisplayType.LOCAL,
    },
    [TimeDisplayType.UTC]: {
        label: 'Coordinated Universal Time',
        icon: 'm-clock',
        type: TimeDisplayType.UTC,
    },
};

export interface TimeDisplay {
    label: string;
    icon: string;
    utcLabel: string;
    time: string;
    type: TimeDisplayType;
    utcTimezoneInHours?: number;
    utcTimezoneOffset?: number;
}

@Injectable({
    providedIn: 'root',
})
export class TimeDisplayService {
    currentDisplayTime: TimeDisplay = {
        label: TimeDisplayData[TimeDisplayType.LOCAL].label,
        icon: TimeDisplayData[TimeDisplayType.LOCAL].icon,
        type: TimeDisplayData[TimeDisplayType.LOCAL].type,
        utcLabel: this.getTimezone().label,
        utcTimezoneInHours: this.getTimezone().hours,
        utcTimezoneOffset: this.getTimezone().offset,
        time: this.getBrowserTime(),
    };

    options: TimeDisplay[] = Object.values(TimeDisplayType).map((option) => {
        const { label, icon, type } = TimeDisplayData[option];
        return {
            label,
            icon,
            type,
            time: null,
            utcLabel: null,
        };
    });

    timeObservable$: Observable<TimeDisplay>;
    timeObservables$: Observable<TimeDisplay[]>;

    selectedTime$ = new BehaviorSubject<TimeDisplay>(this.currentDisplayTime);

    getTime() {
        return this.selectedTime$.getValue();
    }

    constructor() {
        this.startTimer();
    }

    startTimer() {
        // Calculate the initial delay to the next minute
        const initialDelay = 60000 - new Date().getSeconds() * 1000;

        // Create a shared timer that starts after the initial delay and emits a value every 60 seconds
        const sharedTimer$ = timer(initialDelay, 60000).pipe(share());

        // Observable that emits the current display time (visible in the top bar component)
        this.timeObservable$ = this.selectedTime$.pipe(
            distinctUntilChanged(),
            switchMap((selectedTime) =>
                sharedTimer$.pipe(
                    startWith(this.currentDisplayTime),
                    map(() => this.updateTimeDisplay(selectedTime))
                )
            )
        );

        // Observable that emits the time options (visible in the menu that is opened from the top bar component)
        this.timeObservables$ = this.selectedTime$.pipe(
            switchMap(() =>
                sharedTimer$.pipe(
                    startWith(this.options),
                    map(() => {
                        this.updateTimeDisplayOptions();
                        return this.options;
                    })
                )
            )
        );
    }

    selectTime(time: TimeDisplay) {
        this.selectedTime$.next(time);
    }

    updateTimeDisplay(selectedTime: TimeDisplay): TimeDisplay {
        return {
            label: selectedTime.type === TimeDisplayType.UTC ? 'UTC' : selectedTime.label, // When selected as UTC, the label should be 'UTC' instead of 'Universal Time Coordinated'
            icon: this.getDisplayedIconForTime(selectedTime),
            time: this.getDisplayedTime(selectedTime),
            utcLabel: this.getDisplayedUtcLabel(selectedTime),
            utcTimezoneInHours: selectedTime.type === TimeDisplayType.UTC ? 0 : this.getTimezone().hours,
            utcTimezoneOffset: selectedTime.type === TimeDisplayType.UTC ? 0 : this.getTimezone().offset,
            type: selectedTime.type,
        };
    }

    getDisplayedTime(selectedTime: TimeDisplay): string {
        switch (selectedTime.type) {
            case TimeDisplayType.LOCAL:
                return this.getBrowserTime();
            case TimeDisplayType.UTC:
                return this.getCurrentUtcTime();
            default:
                return this.getBrowserTime();
        }
    }

    getDisplayedUtcLabel(selectedTime: TimeDisplay): string {
        switch (selectedTime.type) {
            case TimeDisplayType.LOCAL:
                return this.getTimezone().label;
            case TimeDisplayType.UTC:
                return '(UTC+0)';
            default:
                return this.getTimezone().label;
        }
    }

    getDisplayedIconForTime(selectedTime: TimeDisplay): string {
        switch (selectedTime.type) {
            case TimeDisplayType.LOCAL:
                return TimeDisplayData[TimeDisplayType.LOCAL].icon;
            case TimeDisplayType.UTC:
                return TimeDisplayData[TimeDisplayType.UTC].icon;
            default:
                return TimeDisplayData[TimeDisplayType.LOCAL].icon;
        }
    }

    updateTimeDisplayOptions(): void {
        this.options.forEach((option) => {
            switch (option.type) {
                case TimeDisplayType.UTC:
                    option.time = this.getCurrentUtcTime();
                    option.utcLabel = '(UTC+0)';
                    option.type = TimeDisplayType.UTC;
                    option.utcTimezoneInHours = 0;
                    option.utcTimezoneOffset = 0;
                    break;
                case TimeDisplayType.LOCAL:
                    option.time = this.getBrowserTime();
                    option.utcLabel = this.getTimezone().label;
                    option.utcTimezoneInHours = this.getTimezone().hours;
                    option.utcTimezoneOffset = this.getTimezone().offset;
                    option.type = TimeDisplayType.LOCAL;
                    break;
            }
        });
    }

    getBrowserTime(): string {
        const now = new Date();
        const hours = now.getHours().toString().padStart(2, '0');
        const minutes = now.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    }

    getTimezone(): { label: string; hours: number; offset: number } {
        const timezoneOffsetInMinutes = new Date().getTimezoneOffset();
        const timezoneOffsetInHours = timezoneOffsetInMinutes / -60;
        const sign = timezoneOffsetInHours > 0 ? '+' : '-';
        const utcHours = Math.abs(timezoneOffsetInHours);
        // Adjust -0 to be 0
        const timezoneOffInHoursFormated = Object.is(timezoneOffsetInHours, -0) ? utcHours : timezoneOffsetInHours;

        return { label: `(UTC${sign}${utcHours})`, hours: timezoneOffInHoursFormated, offset: timezoneOffsetInMinutes };
    }

    getCurrentUtcTime() {
        const date = new Date();
        const hours = date.getUTCHours().toString().padStart(2, '0');
        const minutes = date.getUTCMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    }
}
